import type { RecordingCaptionUtterance, RecordingCaptionsResponse } from "@/types/RecordingCaptions";

/**
 * LSP-21125: pure rules behind the recording lightbox's Translation panel, kept out of the component so
 * the boundaries (which row is active, what a row shows) are testable directly.
 */

export type UtteranceDisplay = {
	/** What the row shows as its main line. */
	text: string;
	/** The original beneath it, in italics — null when no translation is shown, or it would repeat `text`. */
	original: string | null;
};

function hasText(value: string | undefined | null): value is string {
	return typeof value === "string" && value.trim().length > 0;
}

/** True only when a response carries at least one row to show — the Translation toggle's gate. */
export function hasCaptionLines(captions: RecordingCaptionsResponse | null | undefined): boolean {
	return !!captions?.hasLines && Array.isArray(captions.utterances) && captions.utterances.length > 0;
}

/**
 * Index of the utterance the playhead is in: the LAST one whose start is at or before `currentTimeSeconds`.
 * -1 before the first utterance. Utterances arrive sorted by `start`.
 */
export function findActiveUtteranceIndex(utterances: RecordingCaptionUtterance[], currentTimeSeconds: number): number {
	if (!Number.isFinite(currentTimeSeconds)) return -1;
	let activeIndex = -1;
	for (let index = 0; index < utterances.length; index += 1) {
		if (utterances[index].start > currentTimeSeconds) break;
		activeIndex = index;
	}
	return activeIndex;
}

/**
 * What one row shows. There is no language picker: only the host's lines are saved, and every translation in
 * them is into the language the host read the call in (`hostLanguage`). So a row shows
 * `translations[hostLanguage] ?? its first translation ?? its original`, with the original beneath whenever a
 * translation is shown. An utterance with no translation — the host's own words, or a same-language call
 * (`hostLanguage` null) — shows its original alone, unmarked: nothing is missing.
 */
export function resolveUtteranceDisplay(utterance: RecordingCaptionUtterance, hostLanguage: string | null): UtteranceDisplay {
	const original = hasText(utterance.original) ? utterance.original : null;
	// Spoken in the host's language already: show it as said, never a translation out of the host's language.
	if (original && hostLanguage && utterance.originalLanguage === hostLanguage) {
		return { text: original, original: null };
	}
	const hostTranslation = hostLanguage ? utterance.translations?.[hostLanguage] : undefined;
	const translation = hasText(hostTranslation) ? hostTranslation : Object.values(utterance.translations ?? {}).find(hasText);
	if (translation) {
		return { text: translation, original: original && original !== translation ? original : null };
	}
	return { text: original ?? "", original: null };
}

/** "Spanish" for "es"; the code itself when the runtime has no name for it. */
export function captionLanguageName(language: string): string {
	try {
		return new Intl.DisplayNames(["en"], { type: "language" }).of(language) ?? language;
	} catch {
		return language;
	}
}

/** Up to two initials for the speaker avatar. */
export function speakerInitials(speakerName: string): string {
	const words = speakerName
		.replace(/\(.*?\)/g, "")
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	return words
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase() ?? "")
		.join("");
}
